import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
  getMarketingConsentText,
} from '@axo/form/configs';
import type { ReloadedFormData } from '@axo/form/data-access/backend-sync';
import { Purpose } from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/types';
import {
  reloadFrontendToggles,
  saveFrontendToggles,
} from '../../../../shared/mutators/frontendToggles';
import { removeCoAppData } from '../../../../shared/mutators/removeCoAppData';
import { transformIncome } from '../../../../shared/mutators/transformIncomes';
import {
  mapNumberInputsToString,
  mapRadioInputsToBoolean,
  mapRadioInputsToString,
  mapStringInputsToNumber,
} from '../../../../utils';
import type { SwedenFormData } from '../FormData.se.types';

const frontendToggles = [
  'Frontend/AddAccountNumber',
  'Frontend/AddCoApplicant',
] as const;
const booleanRadioInputs = ['LoanApplication/CoApplicantIsCohabitant'] as const;
const numericStringInputs = [
  'Debt/Unspecified/RefinanceAmount',
  'Debt/Unspecified/MonthlyDebtPayment',
  'LoanApplication/RentCostMonthly',
  'LoanApplication/CoApplicantRentCostMonthly',
] as const;

const staticValues = {
  'LoanApplication/MarketCountry': MarketCountryCode.Sweden,

  'Person/MarketCountry': MarketCountryCode.Sweden,
  'CoPerson/MarketCountry': MarketCountryCode.Sweden,

  'Customer/MarketCountry': MarketCountryCode.Sweden,
  'Customer/MobilePhoneCountryCode': 46,

  'CoCustomer/MarketCountry': MarketCountryCode.Sweden,
  'CoCustomer/MobilePhoneCountryCode': 46,
};

// Map data from Backend Values to Frontend Values
const fromApi = (data: ReloadedFormData): SwedenFormData => ({
  ...data,
  // Shared
  ...mapNumberInputsToString(data, numericStringInputs),
  ...mapRadioInputsToString(data, booleanRadioInputs),
  ...reloadFrontendToggles(data),
  ...transformIncome(data, 'fromApi'),
  // Market specific
  ...reloadAccountNumberToggle(data),
});

const reloadAccountNumberToggle = (data: ReloadedFormData) => {
  const result = {};
  if (
    'LoanApplication/AccountNumber' in data ||
    'LoanApplication/AccountClearingNumber' in data
  ) {
    Object.assign(result, {
      'Frontend/AddAccountNumber': 'Yes',
    });
  }

  return result;
};

// TODO: remove when we are using the new consent service
// note, consent is a part of customer, but is handled in a separate handler
const addConsentFields = (data: SwedenFormData) => {
  return {
    'MarketingConsent/MarketingConsent': data['Ignore/MarketingConsent']
      ? {
          Version: 10,
          ConsentText: getMarketingConsentText('se'),
          Partners: getMarketingConsentPartners('se'),
          Services: getMarketingConsentServices('se'),
        }
      : {},
  };
};

// Map data from Frontend Values to Backend Values
const toApi = (data: SwedenFormData): SwedenFormData => ({
  ...data,
  // Shared
  ...mapRadioInputsToBoolean(data, booleanRadioInputs),
  ...mapStringInputsToNumber(data, numericStringInputs),
  ...removeCoAppData(data),
  ...saveFrontendToggles(data, frontendToggles),
  ...transformIncome(data, 'toApi'),
  // Market specific
  ...staticValues,
  ...addConsentFields(data),
  ...addCoAppHousingType(data),
});

const addCoAppHousingType = (data: SwedenFormData) => {
  const result = {};

  if (
    // @ts-expect-error conversion to boolean is done later
    data['LoanApplication/CoApplicantIsCohabitant'] === 'Yes' ||
    data['LoanApplication/CoApplicantIsCohabitant'] === true
  ) {
    Object.assign(result, {
      'LoanApplication/CoApplicantHousingType':
        data['LoanApplication/HousingType'],
    });
  }

  return result;
};

const toApiLastSync = (data: SwedenFormData) => {
  const result = {};
  if (data['LoanApplication/InitialPurpose'] === Purpose.RefinancingLoan) {
    const refinanceAmount = Number(data['Debt/Unspecified/RefinanceAmount']);
    if (refinanceAmount > 0) {
      Object.assign(result, {
        'LoanApplication/TotalRefinancingAmount': refinanceAmount,
      });
    }
  }

  if (
    'LoanApplication/InitialPurpose' in data &&
    !data['LoanApplication/Purpose']
  ) {
    Object.assign(result, {
      'LoanApplication/Purpose': data['LoanApplication/InitialPurpose'],
    });
  }

  return result;
};

export const mutators = {
  fromApi,
  toApi,
  toApiLastSync,
};
